import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Hand, Clock, Plus, Scissors, Paintbrush, UtensilsCrossed, MessagesSquare, ShieldCheck, Wrench, ShoppingBag, PenTool, Users } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { PILL_BTN } from "@/lib/pill";
import { gardienById, GARDIENS, COLLECTIONS } from "@/data/gardiens";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const HERO_IMG = "https://images.unsplash.com/photo-1586324304780-c9a5031a3599";
const TEXTURE_IMG = "https://images.unsplash.com/photo-1634393654272-9f6b168356fd";

const DEFAULTS = {
  home_hero_eyebrow: "Objets personnalisés de soin non médicamenteux",
  home_hero_title: "Des objets qui\nprennent soin\nde ceux qu'on aime.",
  home_hero_subtitle: "Pensés pour les personnes atteintes d'Alzheimer, d'autisme et de maladies dégénératives — ou pour tout particulier en quête de douceur.",
  home_hero_image: HERO_IMG,
  home_manifesto_eyebrow: "Notre conviction",
  home_manifesto_title: "Le soin ne passe pas seulement par les médicaments. Il passe aussi par les mains, la matière et la mémoire.",
  home_manifesto_text: "Chaque objet est conçu à partir de l'histoire d'une personne : ses souvenirs, ses besoins sensoriels, son rythme. Une approche non médicamenteuse, chaleureuse et profondément humaine.",
  home_manifesto_image: TEXTURE_IMG,
};

const chapters = [
  { n: "01", icon: Heart, t: "Raviver la mémoire", d: "Des objets porteurs de souvenirs qui réveillent en douceur les émotions et les histoires d'une vie." },
  { n: "02", icon: Hand, t: "Apaiser les sens", d: "Textures, poids et matières pensés pour rassurer, ancrer et réduire l'anxiété au quotidien." },
  { n: "03", icon: Clock, t: "Restaurer les repères", d: "Des créations qui redonnent des points d'appui dans le temps et l'espace, avec bienveillance." },
];

const emile = gardienById("emile");
const leontine = gardienById("leontine");
const marianne = gardienById("marianne");
const emileHobbies = [
  { icon: Scissors, label: "Couture" },
  { icon: Paintbrush, label: "Peinture" },
  { icon: UtensilsCrossed, label: "Alimentation" },
];
const emilePoints = [
  { icon: MessagesSquare, t: "Co-construit avec la famille, par email", d: "Vous nous racontez le besoin, nous avançons ensemble : chaque étape de la création vous est partagée en temps réel, du premier croquis à l'objet fini." },
  { icon: ShieldCheck, t: "Des matériaux garantis", d: "Résistants ou aptes au contact alimentaire selon l'usage, pour un objet sûr et durable au quotidien." },
  { icon: Wrench, t: "À partir d'un objet existant", d: "Le point de départ peut être un objet que votre proche possède déjà mais qui ne correspond plus exactement à ses besoins : notre objectif est de l'adapter parfaitement." },
];


const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [c, setC] = useState(DEFAULTS);
  const [contentLoaded, setContentLoaded] = useState(false);
  const { addToCart } = useApp();

  useEffect(() => { api.get("/portfolio").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/products").then((r) => {
      const all = r.data;
      const preferred = ["bougie-parfum-souvenir", "coeur-resine-souvenir", "boite-souvenirs"];
      const first = preferred.map((s) => all.find((p) => p.slug === s)).filter(Boolean);
      const rest = all.filter((p) => !preferred.includes(p.slug));
      setChosen([...first, ...rest].slice(0, 3));
    }).catch(() => {});
  }, []);
  useEffect(() => { api.get("/content").then((r) => { setC({ ...DEFAULTS, ...r.data }); setContentLoaded(true); }).catch(() => setContentLoaded(true)); }, []);

  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const scroll = () => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    // double passage : le 2e corrige le décalage dû au chargement de l'affiche hero
    const t1 = setTimeout(scroll, 300);
    const t2 = setTimeout(scroll, 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [hash]);

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="relative overflow-hidden grain pt-14 lg:pt-16">
          {contentLoaded ? (
            <motion.img
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
              src={c.home_hero_image} alt="Une petite-fille et sa grand-mère partagent un souvenir encadré, veillées par une gardienne à la lanterne — Passerelle, des objets qui maintiennent le lien quand les souvenirs s'effacent" className="w-full h-auto block" data-testid="hero-image" />
          ) : (
            <div className="w-full aspect-[3/2] bg-[#f8f3e8]" data-testid="hero-image-placeholder" />
          )}
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <h1 style={{ opacity: contentLoaded ? 1 : 0, transition: "opacity 0.6s ease" }} className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.08] tracking-tight max-w-2xl text-foreground/90">
            <MaskedLines lines={c.home_hero_title.split("\n")} />
          </h1>
          {/* Léontine présente les Gardiens */}
          <Reveal delay={0.2}>
            <div className="mt-10 flex items-center gap-5 sm:gap-7 max-w-3xl" data-testid="leontine-intro">
              <img src={leontine.image} alt="Léontine, la Semeuse de Sourires" data-testid="leontine-medallion"
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover object-top border-4 shrink-0 shadow-[0_14px_40px_rgb(0,0,0,0.14)]"
                style={{ borderColor: `${leontine.color}55` }} />
              <div>
                <p className="font-serif-display italic text-xl sm:text-2xl leading-snug text-foreground/85">
                  « Je suis Léontine, la Semeuse de Sourires — chez nous, c'est moi qui veille sur la joie de vivre. »
                </p>
                <p className="mt-3 text-foreground/70 leading-relaxed">
                  Avec mes seize compagnons — Gardiens des Souvenirs et Gardiens des Forces Intérieures — nous veillons sur ceux que la vie rend fragiles. Chacun de nous garde une émotion précieuse et accompagne un chemin de soin tout en douceur. Laissez-moi vous les présenter…
                </p>
                <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.2em]" style={{ color: leontine.color }}>Léontine · Gardienne de l'accueil</p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-10" data-testid="hero-actions">
            {[
              { href: "#creations-emile", testid: "hero-badge", icon: Hand, label: "Création d'objets personnalisés" },
              { to: "/boutique", testid: "hero-shop-btn", icon: ShoppingBag, label: "Découvrir la boutique" },
              { to: "/contact", testid: "hero-custom-btn", icon: PenTool, label: "Demande sur mesure" },
              { to: "/gardiens", testid: "hero-gardiens-btn", icon: Users, label: "Les Gardiens" },
            ].map(({ href, to, testid, icon: Icon, label }) => {
              const cls = `${PILL_BTN} w-full h-full justify-center text-center`;
              const inner = (<><Icon className="w-4 h-4 shrink-0" /> {label} <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-1 transition-transform" /></>);
              return to
                ? <Link key={testid} to={to} data-testid={testid} className={cls}>{inner}</Link>
                : <a key={testid} href={href} data-testid={testid} className={cls}>{inner}</a>;
            })}
          </div>
        </div>
      </section>

      {/* PHRASE (fixe) */}
      <div className="border-y border-border/40 bg-secondary/15 py-7 px-6 text-center" data-testid="hero-marquee">
        <p className="font-serif-display italic text-xl lg:text-2xl text-foreground/75 max-w-4xl mx-auto leading-relaxed">
          {c.home_hero_subtitle}
        </p>
      </div>

      {/* CHAQUE GARDIEN, SA PATHOLOGIE, SA THÉRAPIE */}
      <section className="bg-accent/30 py-28" data-testid="home-gardiens-care">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] text-warm mb-5">Accompagnement doux</p>
            <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light leading-tight max-w-3xl">Chaque Gardien veille sur un chemin de soin</h2>
            <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-2xl">
              À chaque pathologie, son Gardien et sa thérapie douce — non médicamenteuse, chaleureuse et profondément humaine.
            </p>
          </Reveal>
          {COLLECTIONS.map((col) => (
            <div key={col.id} className="mt-16">
              <Reveal>
                <h3 className="font-serif-display text-2xl sm:text-3xl text-foreground/85 flex items-center gap-4">
                  {col.label}
                  <span className="flex-1 h-px bg-warm/30" />
                </h3>
              </Reveal>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
                {GARDIENS.filter((g) => g.collection === col.id).map((g, i) => (
                  <Reveal key={g.id} delay={(i % 4) * 0.07}>
                    <Link to={`/gardiens/${g.id}`} data-testid={`home-gardien-care-${g.id}`}
                      className="group block bg-card rounded-[1.75rem] border border-warm/25 p-6 text-center h-full hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.09)] transition-all duration-500">
                      <div className="relative mx-auto w-24 h-24">
                        <div className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 3px ${g.color}33` }} />
                        <img src={g.image} alt={`${g.name} — ${g.title}`} className="w-24 h-24 rounded-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <p className="mt-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: g.color }}>{g.value}</p>
                      <p className="font-serif-display text-xl mt-1">{g.name}</p>
                      <div className="mt-4 pt-4 border-t border-border/50 space-y-1.5 text-xs leading-relaxed">
                        <p className="text-foreground/70"><span className="font-medium text-foreground/85">Pathologie :</span> {g.pathologie}</p>
                        <p className="text-foreground/70"><span className="font-medium text-foreground/85">Thérapie :</span> {g.therapie}</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LES GARDIENS DES SOUVENIRS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28" data-testid="home-gardiens">
        <div className="grid lg:grid-cols-12 gap-12 items-center rounded-[2.5rem] bg-warm/10 border border-warm/25 p-8 sm:p-14 grain overflow-hidden">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="grid grid-cols-4 gap-4">
                {["eloise", "arsene", "gaston", "josephine", "celestine", "theodore", "agathe", "victor"].map((id) => {
                  const g = gardienById(id);
                  return (
                    <img key={id} src={g.image} alt={g.name} title={`${g.name} — ${g.value}`}
                      className="rounded-full w-full aspect-square object-cover object-top border-2 border-warm/30 hover:scale-105 transition-transform duration-500" />
                  );
                })}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-sm uppercase tracking-[0.3em] text-warm mb-5">Nos dix-sept Gardiens</p>
              <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light leading-tight">Un jardin de belles émotions</h2>
              <p className="mt-6 text-lg text-foreground/70 leading-relaxed">
                Éloïse, Arsène, Madeleine et leurs amis veillent chacun sur une émotion précieuse ou une force intérieure.
                En cinq questions, découvrez quel Gardien accompagnera le mieux votre proche —
                avec des activités et des objets choisis pour lui ou elle.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/quiz-gardien" data-testid="home-quiz-btn" className={PILL_BTN}>
                  Trouver son Gardien <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/gardiens" data-testid="home-gardiens-btn" className={PILL_BTN}>
                  Rencontrer les Gardiens
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-8 sm:gap-12 text-center sm:text-left" data-testid="manifesto-block">
          <Reveal>
            <img src={marianne.image} alt="Marianne, la Gardienne du Réconfort" data-testid="marianne-manifesto-medallion"
              className="w-32 h-32 sm:w-44 sm:h-44 rounded-full object-cover object-top border-4 shrink-0 shadow-[0_14px_40px_rgb(0,0,0,0.14)]"
              style={{ borderColor: `${marianne.color}55` }} />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm uppercase tracking-[0.3em] text-primary mb-6">{c.home_manifesto_eyebrow}</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight">
              {c.home_manifesto_title}
            </h2>
            <p className="mt-8 text-lg text-foreground/70 leading-relaxed">
              {c.home_manifesto_text}
            </p>
            <p className="mt-6 text-xs sm:text-sm uppercase tracking-[0.2em]" style={{ color: marianne.color }}>Marianne · La Gardienne du Réconfort</p>
          </Reveal>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="bg-accent/30 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><h2 className="font-serif-display text-4xl sm:text-5xl font-light mb-16 max-w-2xl">Trois façons d'accompagner en douceur</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="bg-card rounded-3xl border border-border/50 p-10 h-full hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <span className="font-serif-display text-5xl text-primary/30">{c.n}</span>
                    <c.icon className="w-7 h-7 text-secondary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif-display text-2xl mt-8">{c.t}</h3>
                  <p className="mt-4 text-foreground/70 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CRÉATIONS PERSONNALISÉES AVEC ÉMILE */}
      <section id="creations-emile" className="max-w-7xl mx-auto px-6 lg:px-10 py-28 scroll-mt-20" data-testid="home-emile-sur-mesure">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center rounded-[2.5rem] border p-8 sm:p-14 grain overflow-hidden" style={{ backgroundColor: `${emile.color}14`, borderColor: `${emile.color}40` }}>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative max-w-sm mx-auto">
                <img src={emile.image} alt="Émile — Le Gardien de l'Adaptation" data-testid="emile-portrait"
                  className="w-full aspect-[4/5] object-cover object-top rounded-[2rem] border-4 shadow-[0_24px_50px_rgb(0,0,0,0.12)]" style={{ borderColor: `${emile.color}55` }} />
                <span className="absolute top-4 left-4 bg-background/85 backdrop-blur text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full" style={{ color: emile.color }} data-testid="emile-value">{emile.value}</span>
                <img src={emile.familiar.image} alt={`${emile.familiar.name}, ${emile.familiar.title}`} title={`${emile.familiar.name}, ${emile.familiar.title}`}
                  className="absolute -bottom-7 -right-5 w-24 h-24 rounded-full object-cover border-4 border-background shadow-lg" />
              </div>
              <div className="mt-12 text-center max-w-sm mx-auto">
                <p className="font-serif-display text-2xl">{emile.name} <span className="text-sm text-muted-foreground font-sans">· {emile.title}</span></p>
                <p className="font-serif-display italic text-foreground/75 mt-2 leading-snug">« {emile.quote} »</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-sm uppercase tracking-[0.3em] mb-5" style={{ color: emile.color }}>Objets sur mesure</p>
              <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light leading-tight" data-testid="emile-section-title">Créations personnalisées avec Émile</h2>
              <p className="mt-6 text-lg text-foreground/70 leading-relaxed">
                Un couteau devenu difficile à tenir, un pinceau qui glisse, une aiguille qui pique les doigts…
                Nous créons des objets ergonomiques adaptés aux hobbies de votre proche et à sa morphologie ou à son handicap, pour que les gestes qu'il ou elle aime restent possibles.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                {emileHobbies.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2 rounded-full bg-background/70 border px-4 py-2 text-sm" style={{ borderColor: `${emile.color}35` }}>
                    <Icon className="w-4 h-4" style={{ color: emile.color }} /> {label}
                  </span>
                ))}
                <span className="inline-flex items-center rounded-full bg-background/70 border px-4 py-2 text-sm text-foreground/60" style={{ borderColor: `${emile.color}35` }}>et bien d'autres…</span>
              </div>
              <div className="mt-9 space-y-6">
                {emilePoints.map(({ icon: Icon, t, d }) => (
                  <div key={t} className="flex gap-4">
                    <div className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center bg-background/80 border" style={{ borderColor: `${emile.color}35` }}>
                      <Icon className="w-5 h-5" style={{ color: emile.color }} strokeWidth={1.7} />
                    </div>
                    <div>
                      <h3 className="font-serif-display text-xl">{t}</h3>
                      <p className="mt-1 text-foreground/70 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link to="/contact" data-testid="emile-contact-btn" className={PILL_BTN}>
                  Nous contacter pour votre projet <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <p className="text-sm text-foreground/60">Réponse personnelle sous 48h.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PIÈCES CHOISIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28" data-testid="home-featured-products">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal><h2 className="font-serif-display text-4xl sm:text-5xl font-light">Pièces choisies</h2></Reveal>
          <Reveal delay={0.1}>
            <Link to="/boutique" className="group flex items-center gap-2 text-primary" data-testid="see-all-products">
              Voir toute la boutique <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {chosen.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <article data-testid={`home-product-${p.slug}`} className="group bg-card rounded-3xl border border-border/50 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-500 h-full flex flex-col">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 bg-background/85 backdrop-blur text-xs px-3 py-1.5 rounded-full">{p.condition}</span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-secondary-foreground/60">{p.category}</p>
                  <h3 className="font-serif-display text-2xl mt-2">{p.name}</h3>
                  <div className="flex items-center justify-between mt-6 flex-1 items-end">
                    <span className="font-serif-display text-3xl">{p.price.toFixed(0)} €</span>
                    <button
                      data-testid={`home-add-to-cart-${p.slug}`}
                      onClick={() => addToCart(p)}
                      className="rounded-full bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                      aria-label={`Ajouter ${p.name} au panier`}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PORTFOLIO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal><h2 className="font-serif-display text-4xl sm:text-5xl font-light">Réalisations sur demande</h2></Reveal>
          <Reveal delay={0.1}>
            <Link to="/realisations" className="group flex items-center gap-2 text-primary" data-testid="see-all-portfolio">
              Voir toutes les créations <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {featured.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.08} className={i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}>
              <div className="group relative overflow-hidden rounded-3xl h-full min-h-[280px]">
                <img src={f.image} alt={f.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <p className="text-background/70 text-xs uppercase tracking-[0.2em]">{f.category}</p>
                  <h3 className="font-serif-display text-2xl text-background mt-2">{f.title}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <Reveal>
          <div className="rounded-[2.5rem] bg-warm text-warm-foreground px-8 py-20 text-center grain overflow-hidden">
            <h2 className="font-serif-display text-4xl sm:text-6xl font-light max-w-3xl mx-auto leading-tight">Une histoire, un besoin ? Créons ensemble l'objet juste.</h2>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-10 rounded-full bg-background text-foreground px-9 py-4 hover:scale-105 transition-transform duration-300" data-testid="cta-custom-btn">
              Faire une demande personnalisée <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
